import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Phone, Shield, Globe, Compass, UserCheck, ChevronRight } from "lucide-react";

const HERO_IMG = "/polar-bear-hero.jpg";

const STATS = [
  { value: "30+", label: "Years of Experience" },
  { value: "6", label: "Continents" },
  { value: "200+", label: "Hunts Coordinated" },
  { value: "50+", label: "Vetted Outfitters" },
];

const EXPEDITIONS = [
  {
    title: "Tajikistan Marco Polo",
    species: "Marco Polo Sheep",
    region: "Central Asia",
    season: "Jul — Nov",
    price: "$46,000+",
    image: "/marco-polo-sheep.jpg",
    tag: "Limited",
  },
  {
    title: "Alaska Brown Bear",
    species: "Coastal Brown Bear",
    region: "Alaska Peninsula",
    season: "Apr — May, Sep — Oct",
    price: "$25,000+",
    image: "https://dispatchhunts.com/wp-content/uploads/2024/12/Alaskan-fall-brown-bear-hunt.jpg",
  },
  {
    title: "Montana Elk",
    species: "Rocky Mountain Elk",
    region: "Western Montana",
    season: "Sep — Nov",
    price: "$8,500+",
    image: "https://dispatchhunts.com/wp-content/uploads/2024/12/montana-elk-rifle-hunt.jpg",
  },
  {
    title: "Louisiana Alligator",
    species: "American Alligator",
    region: "Louisiana Bayou",
    season: "Sep — Oct",
    price: "$3,500+",
    image: "https://images.unsplash.com/photo-1641682701238-428d30aba9a3?w=800&h=600&fit=crop",
  },
  {
    title: "Texas Hog Hunt",
    species: "Feral Hog / Wild Boar",
    region: "South Texas",
    season: "Year-round",
    price: "$1,500+",
    image: "https://images.unsplash.com/photo-1568669350060-3ae3599c702a?w=800&h=600&fit=crop",
  },
  {
    title: "Costa Rica Marlin",
    species: "Blue Marlin, Sailfish",
    region: "Pacific Coast, CR",
    season: "Dec — Apr",
    price: "$3,500+",
    image: "https://images.unsplash.com/photo-1711130361962-e96796065a6d?w=800&h=600&fit=crop",
  },
  {
    title: "Montana Fly Fishing",
    species: "Rainbow & Brown Trout",
    region: "Missouri & Madison Rivers",
    season: "Jun — Oct",
    price: "$3,500+",
    image: "https://images.unsplash.com/photo-1602608782622-5922b461a9cc?w=800&h=600&fit=crop",
  },
  {
    title: "Kamchatka Brown Bear",
    species: "Kamchatka Brown Bear",
    region: "Russia — Kamchatka",
    season: "May — Jun, Sep — Oct",
    price: "$25,000+",
    image: "https://dispatchhunts.com/wp-content/uploads/2024/12/Brown-Bear-Kamchatka.jpg",
    tag: "Elite",
  },
  {
    title: "South Africa Plains Game",
    species: "Kudu, Gemsbok, Impala +",
    region: "Eastern Cape, SA",
    season: "Apr — Oct",
    price: "$4,500+",
    image: "https://dispatchhunts.com/wp-content/uploads/2025/03/2b3bd60b-3821-40e2-b178-75f16c50775e.jpeg",
  },
  {
    title: "Africa Cape Buffalo",
    species: "Cape Buffalo",
    region: "Tanzania / Zimbabwe",
    season: "Jun — Nov",
    price: "$15,000+",
    image: "https://images.unsplash.com/photo-1582552848181-e14dfec85f7f?w=800&h=600&fit=crop",
    tag: "Dangerous Game",
  },
  {
    title: "Argentina Red Stag",
    species: "Red Stag, Fallow Deer",
    region: "Patagonia, Argentina",
    season: "Mar — Jun",
    price: "$5,000+",
    image: "https://images.unsplash.com/photo-1571145574924-cb7ae38bc13f?w=800&h=600&fit=crop",
  },
  {
    title: "Pakistan Himalayan Ibex",
    species: "Himalayan Ibex",
    region: "Northern Pakistan",
    season: "Oct — Jan",
    price: "$12,000+",
    image: "https://dispatchhunts.com/wp-content/uploads/2025/10/IMG_2826.jpeg",
  },
  {
    title: "Western Mule Deer",
    species: "Trophy Mule Deer",
    region: "Colorado / Wyoming",
    season: "Oct — Nov",
    price: "$5,000+",
    image: "https://images.unsplash.com/photo-1731179146362-5a27b6eee030?w=800&h=600&fit=crop",
  },
  {
    title: "Mongolia Argali",
    species: "Altai Argali Sheep",
    region: "Altai Mountains",
    season: "Jul — Sep",
    price: "$145,000+",
    image: "/marco-polo-sheep.jpg",
    tag: "Ultra-Elite",
  },
  {
    title: "New Zealand Red Stag",
    species: "Red Stag, Tahr, Chamois",
    region: "South Island, NZ",
    season: "Mar — Aug",
    price: "$7,500+",
    image: "https://images.unsplash.com/photo-1621684897951-65ff5c6661a2?w=800&h=600&fit=crop",
  },
];

const WHY_BLOCKS = [
  {
    icon: UserCheck,
    title: "We do the legwork.",
    text: "Every hunt is personally vetted. Our advisors have been to these camps, met these outfitters, and hunted these areas. You're not getting a recommendation from a sales desk.",
  },
  {
    icon: Shield,
    title: "Vetted outfitters only.",
    text: "We work with a tight network of proven operators. If they're not delivering, they're out. Your hunt is too expensive to gamble on an unknown outfit.",
  },
  {
    icon: Globe,
    title: "Everything handled.",
    text: "Flights, licensing, gear lists, travel logistics, deposits, outfitter communication. You show up ready to hunt. We've got everything else.",
  },
  {
    icon: Compass,
    title: "No surprises.",
    text: "You'll know exactly what to expect. Terrain, physical demands, weather, accommodations, what's included, what's not. Straight talk before you book.",
  },
];

const STEPS = [
  { num: "01", title: "Apply", desc: "Tell us what you're after." },
  { num: "02", title: "Talk to us", desc: "A real conversation, not a sales pitch." },
  { num: "03", title: "Get matched", desc: "The right hunt, outfitter, and timing." },
  { num: "04", title: "Book it", desc: "Deposit locks your spot. We handle the rest." },
  { num: "05", title: "Hunt", desc: "You focus on the moment." },
  { num: "06", title: "Come back", desc: "Debrief. Plan the next one." },
];

const TESTIMONIALS = [
  {
    quote: "Matt put me on a 380-class bull in Montana that I'd been chasing for three years. The outfitter was dialed in, camp was solid, and the whole trip ran like clockwork.",
    name: "R. Thompson",
    location: "Texas",
    hunt: "Montana Elk",
  },
  {
    quote: "I'd never hunted Africa. Matt walked me through everything — shot list to travel logistics. Ended up taking seven species in South Africa. Already planning the next one.",
    name: "J. Mitchell",
    location: "Colorado",
    hunt: "South Africa Plains Game",
  },
  {
    quote: "I've used three different brokers over the years. Dispatch Hunts is the only one where I felt like the guy on the other end actually cared about my hunt.",
    name: "D. Wilson",
    location: "Montana",
    hunt: "Alaska Brown Bear",
  },
];

export default function Home() {
  return (
    <>
      <Helmet>
        <title>Dispatch Hunts — Private Hunt Advisory for Serious Sportsmen</title>
        <meta name="description" content="Dispatch Hunts connects serious hunters with vetted outfitters across six continents. Personalized guidance from experienced advisors. No surprises." />
      </Helmet>

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Marco Polo sheep hunt in Tajikistan" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-carbon/90 via-carbon/70 to-carbon/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon/60 via-transparent to-carbon/20" />
        </div>
        <div className="container-dh relative z-10 pt-32 pb-20">
          <div className="max-w-2xl">
            <p className="text-label text-brass mb-6 opacity-0 animate-fade-up" data-testid="hero-label">
              Private Hunt Advisory — Worldwide
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-bone leading-[1.1] mb-6 opacity-0 animate-fade-up stagger-1" data-testid="hero-headline">
              Your hunt is too important to leave to chance.
            </h1>
            <p className="font-body text-lg text-bone/70 leading-relaxed mb-10 max-w-xl opacity-0 animate-fade-up stagger-2">
              The Serengeti. The Pamirs at 15,000 feet. Polar ice at the end of the earth. Our advisors have been there — six continents, 200+ coordinated expeditions, every outfitter in our network personally vetted.
            </p>
            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-up stagger-3">
              <Link to="/apply" className="btn-brass" data-testid="hero-apply-btn">
                Apply for a Consultation <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/expeditions" className="btn-outline" data-testid="hero-expeditions-btn">
                View Expeditions
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ PROOF BAR ═══ */}
      <section className="section-dark grain py-8" data-testid="proof-bar">
        <div className="container-dh">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {STATS.map((s) => (
              <div key={s.label}>
                <p className="font-display text-3xl sm:text-4xl font-bold text-brass">{s.value}</p>
                <p className="text-label text-bone/40 mt-1 text-[10px]">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FEATURED EXPEDITIONS ═══ */}
      <section className="section-chalk py-20 lg:py-28" data-testid="expeditions-section">
        <div className="container-dh">
          <div className="text-center mb-14">
            <p className="text-label text-brass mb-3">Current Opportunities</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-carbon">
              Real hunts. Real availability.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {EXPEDITIONS.map((exp) => (
              <Link
                to="/expeditions"
                key={exp.title}
                className="group block bg-bone overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                data-testid={`expedition-card-${exp.title.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={exp.image}
                    alt={exp.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-carbon/60 to-transparent" />
                  {exp.tag && (
                    <span className="absolute top-4 right-4 bg-copper text-bone text-label !text-[10px] px-3 py-1">
                      {exp.tag}
                    </span>
                  )}
                  <div className="absolute bottom-4 left-4">
                    <p className="font-display text-xl font-bold text-bone">{exp.title}</p>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-label text-smoke text-[10px]">{exp.region}</span>
                    <span className="font-mono text-sm font-bold text-brass">{exp.price}</span>
                  </div>
                  <p className="text-sm text-slate font-body">{exp.species}</p>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-carbon/5">
                    <span className="text-xs text-smoke font-body">{exp.season}</span>
                    <span className="text-label text-brass text-[10px] group-hover:translate-x-1 transition-transform flex items-center gap-1">
                      Details <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/expeditions" className="btn-outline-dark" data-testid="view-all-expeditions">
              See All Expeditions <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ WHY DISPATCH HUNTS ═══ */}
      <section className="section-dark grain py-20 lg:py-28" data-testid="why-section">
        <div className="container-dh">
          <div className="max-w-xl mb-14">
            <p className="text-label text-brass mb-3">Why Serious Hunters Work With Us</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-bone">
              This isn't a booking engine.<br />It's a trusted advisor.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {WHY_BLOCKS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 bg-brass/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-brass" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold text-bone mb-2">{title}</h3>
                  <p className="font-body text-sm text-bone/60 leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ THE PROCESS ═══ */}
      <section className="section-light py-20 lg:py-28" data-testid="process-section">
        <div className="container-dh">
          <div className="text-center mb-14">
            <p className="text-label text-brass mb-3">How It Works</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-carbon">
              Six steps. Your hunt handled.
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {STEPS.map((step) => (
              <div key={step.num} className="text-center">
                <p className="font-mono text-3xl font-bold text-brass/30 mb-3">{step.num}</p>
                <h4 className="font-display text-base font-bold text-carbon mb-1">{step.title}</h4>
                <p className="text-xs text-smoke font-body">{step.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-14">
            <Link to="/process" className="btn-outline-dark" data-testid="learn-process-btn">
              Learn More About Our Process <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF ═══ */}
      <section className="section-canvas py-20 lg:py-28" data-testid="testimonials-section">
        <div className="container-dh">
          <div className="text-center mb-14">
            <p className="text-label text-brass mb-3">From the Field</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-carbon">
              What clients say.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-bone p-8 shadow-sm" data-testid={`testimonial-${t.name.replace(/\s/g, "-")}`}>
                <p className="font-editorial text-lg text-carbon italic leading-relaxed mb-6">
                  "{t.quote}"
                </p>
                <div className="border-t border-carbon/10 pt-4">
                  <p className="font-label text-sm font-semibold text-carbon tracking-wide">{t.name}</p>
                  <p className="text-xs text-smoke font-body">{t.location} — {t.hunt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ YOUR GUIDES ═══ */}
      <section className="section-dark grain py-20 lg:py-28" data-testid="guides-section">
        <div className="container-dh">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img
                src="https://dispatchhunts.com/wp-content/uploads/2024/12/home-page-brown-bear-Matt.jpg"
                alt="Dispatch Hunts advisors in the field"
                className="w-full aspect-[4/5] object-cover"
                loading="lazy"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-carbon/80 to-transparent p-6">
                <p className="text-label text-brass/60 text-[10px]">Field Advisory Team — Alaska</p>
              </div>
            </div>
            <div>
              <p className="text-label text-brass mb-4">Personalized, Experienced Guides</p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-bone mb-6">
                Most brokers sit behind a desk. We don't.
              </h2>
              <div className="space-y-4 font-body text-bone/60 leading-relaxed">
                <p>
                  The Serengeti. The Pamirs at 15,000 feet. Polar ice at the end of the earth. Our advisors have been there — six continents, 200+ coordinated expeditions, and every outfitter in our network personally vetted.
                </p>
                <p>
                  When you call Dispatch Hunts, you're talking to someone who's been there. Not a call center. Not a sales rep. An experienced advisor who knows the outfitter by name, knows the terrain, and will tell you straight whether a hunt is worth your time and money.
                </p>
                <p className="text-bone/80 font-semibold">
                  That's the difference.
                </p>
              </div>
              <div className="mt-8">
                <Link to="/about" className="btn-outline" data-testid="meet-team-btn">
                  About Our Team <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ DISPATCH CIRCLE TEASER ═══ */}
      <section className="relative py-20 lg:py-28 overflow-hidden" data-testid="dispatch-circle-section">
        <div className="absolute inset-0">
          <img
            src="https://dispatchhunts.com/wp-content/uploads/2024/12/Brown-Bear-Spring-Alaska1.jpg"
            alt="Brown bear hunt Alaska"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-carbon/85" />
        </div>
        <div className="container-dh relative z-10 text-center max-w-3xl mx-auto">
          <p className="text-label text-brass mb-4">For Serious Hunters</p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-bone mb-6">
            The Dispatch Circle
          </h2>
          <p className="font-body text-bone/60 leading-relaxed mb-4 max-w-2xl mx-auto">
            Our private advisory tier for hunters who book $25,000 or more in a calendar year. Priority access to limited hunts. Direct line to our senior advisors. Annual gathering at a premier lodge destination.
          </p>
          <p className="font-body text-bone/40 text-sm mb-10">
            It's not a loyalty program. It's how we take care of our most serious clients.
          </p>
          <Link to="/dispatch-circle" className="btn-brass" data-testid="circle-learn-more">
            Learn About the Circle <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="section-dark grain py-20 lg:py-28" data-testid="final-cta-section">
        <div className="container-dh text-center max-w-2xl mx-auto">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-bone mb-4">
            Ready to talk about your next hunt?
          </h2>
          <p className="font-body text-bone/50 mb-10">
            Apply for a consultation. We'll be in touch within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/apply" className="btn-brass" data-testid="final-apply-btn">
              Apply for a Consultation <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:4065805366" className="btn-outline" data-testid="final-call-btn">
              <Phone className="w-4 h-4" /> Call Us: 406-580-5366
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
